import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import './Call.scss';

import Share from '@material-ui/icons/Share';
import Close from '@material-ui/icons/Close';
import FileCopy from '@material-ui/icons/FileCopy';

import Loader from '../General/Loader';
import CallInsights from './CallInsights';
import CallTranscript from './CallTranscript';
import CallFooter from './CallFooter';

import { copyToClipboard, isShareLink, getUrlParam } from '../../helpers/helpers';

class Call extends Component {
  static propTypes = {
    callId: PropTypes.number,
    callHash: PropTypes.string,
    call: PropTypes.object,
    updateCall: PropTypes.func,
    getCall: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.props.getCall(this.props.callId || this.props.callHash).then((resp) => {
      window.__ADD_EVENT__('Call - Page View', { call: resp.call });
      this.autoScrollIfNecessary();
    });

    // Need to force render of HTML Audio and Video due to the way they are handled in react
    setTimeout(() => this.setState({ canLoadAudioAndVideo: true }), 100);
  }

  state = {
    audio: null,
    audioTime: 0,
    audioPaused: true,
    highlightedEmotion: null,
    highlightedVariable: null,
    highlightedParticipant: null,
    canLoadAudioAndVideo: false,
    togglingShareLink: false,
  };

  autoScrollIfNecessary = () => {
    // If we have a call timestamp in the header, autoscroll there.
    const start = getUrlParam('start');
    start && setTimeout(() => this.setTime(parseFloat(start), true), 400);
  };

  setHighlightedEmotion = (newEmotion) => {
    window.__ADD_EVENT__('Call - Select Emotion', { emotion: newEmotion, call: this.props.call });
    this.setState({ highlightedEmotion: newEmotion });
  };

  setHighlightedVariable = (newVariable) => {
    window.__ADD_EVENT__('Call - Select Statement Type', { emotion: newVariable, call: this.props.call });
    this.setState({ highlightedVariable: newVariable });
  };

  setHighlightedParticipant = (newParticipant) => {
    window.__ADD_EVENT__('Call - Select Participant', { participant: newParticipant, call: this.props.call });
    this.setState({ highlightedParticipant: newParticipant });
  };

  setAudioRef = (ref) => !this.state.audio && this.setState({ audio: ref });
  setVideoRef = (ref) => !this.state.video && this.setState({ video: ref });

  setTimeRatio = (ratio) => this.setTime(this.props.call?.duration_seconds * ratio);
  setTime = (newTime, forcePlay = false) => {
    const { audio, video } = this.state;
    if (!this.confirmReadyToPlay()) return;
    // Handle Audio and Video Components
    if (newTime) audio.currentTime = newTime;
    this.state.video && newTime && video.seek(newTime);
    // Handle State Management
    this.updateTime(newTime);
    forcePlay && this.startTime();
  };
  updateTime = (newTime) => {
    this.setState({ audioTime: newTime });
  };
  startTime = () => {
    if (!this.confirmReadyToPlay()) return;
    this.setState({ audioPaused: false }, () => {
      this.state.audio.play();
      this.state.video && this.state.video.play();
      window.__ADD_EVENT__('Call - Start Media', { call: this.props.call });
    });
  };
  stopTime = () => {
    if (!this.confirmReadyToPlay()) return;
    this.setState({ audioPaused: true }, () => {
      this.state.audio.pause();
      this.state.video && this.state.video.pause();
    });
  };

  confirmReadyToPlay = () => {
    return !this.state.audio
      ? cogoToast.warn('Please wait until the audio has loaded to begin playing.') && false
      : !this.state.video?.video && this.props.call?.recording?.zoomrecording?.video_s3_url
      ? cogoToast.warn('Please wait until the video has loaded to begin playing.') && false
      : true;
  };

  generateShareLink = async () => {
    const { call, updateCall } = this.props;
    window.__ADD_EVENT__('Call - Generate Share Link', { call });
    this.setState({ togglingShareLink: true });
    await updateCall(call, { is_shareable: true });
    this.setState({ togglingShareLink: false });
  };

  removeShareLink = async () => {
    const { call, updateCall } = this.props;
    window.__ADD_EVENT__('Call - Remove Share Link', { call });
    this.setState({ togglingShareLink: true });
    await updateCall(call, { is_shareable: false });
    this.setState({ togglingShareLink: false });
  };

  render() {
    const { call } = this.props;
    const {
      audioTime,
      audioPaused,
      canLoadAudioAndVideo,
      highlightedEmotion,
      highlightedVariable,
      highlightedParticipant,
      togglingShareLink,
    } = this.state;
    const { chat_name, is_shareable, share_hash } = call || {};
    const shareLink = `${window.location.href.split('://')[1]?.split('/')[0]}/s/c/${share_hash}`;
    if (!call?.messages) return null;
    return (
      <>
        <div className='summary-outer-container'>
          <div className='summary-header-container'>
            <h1>{chat_name || '...'}</h1>
            <div className='sharing-section'>
              {is_shareable ? (
                <div className='share-link-container'>
                  <div className='share-link'>{shareLink}</div>
                  <div className='actions'>
                    <div onClick={() => copyToClipboard(shareLink, true)} className='copy action'>
                      <FileCopy />
                    </div>
                    {!isShareLink() && (
                      <div onClick={this.removeShareLink} className='remove action'>
                        {togglingShareLink ? <Loader size={20} /> : <Close />}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div onClick={this.generateShareLink} className='generate-btn'>
                  {togglingShareLink ? <Loader size={20} /> : <Share />}
                  Generate Share Link
                </div>
              )}
            </div>
          </div>
          <div className='summary-inner-container'>
            {call && (
              <>
                <CallInsights
                  call={call}
                  highlightedEmotion={highlightedEmotion}
                  highlightedVariable={highlightedVariable}
                  highlightedParticipant={highlightedParticipant}
                  setHighlightedEmotion={this.setHighlightedEmotion}
                  setHighlightedVariable={this.setHighlightedVariable}
                  setHighlightedParticipant={this.setHighlightedParticipant}
                  audioTime={audioTime}
                  audioPaused={audioPaused}
                  startTime={this.startTime}
                  stopTime={this.stopTime}
                  setVideoRef={this.setVideoRef}
                  video={this.state.video}
                  audio={this.state.audio}
                  canLoadAudioAndVideo={canLoadAudioAndVideo}
                />
                <CallFooter
                  call={call}
                  highlightedEmotion={highlightedEmotion}
                  highlightedParticipant={highlightedParticipant}
                  highlightedVariable={highlightedVariable}
                  setAudioRef={this.setAudioRef}
                  audio={this.state.audio}
                  startTime={this.startTime}
                  stopTime={this.stopTime}
                  updateTime={this.updateTime}
                  setTime={this.setTime}
                  setTimeRatio={this.setTimeRatio}
                  audioTime={audioTime}
                  audioPaused={audioPaused}
                  canLoadAudioAndVideo={canLoadAudioAndVideo}
                />
                <CallTranscript
                  call={call}
                  audioTime={audioTime}
                  audioPaused={audioPaused}
                  highlightedVariable={highlightedVariable}
                  highlightedEmotion={highlightedEmotion}
                  highlightedParticipant={highlightedParticipant}
                  setHighlightedEmotion={this.setHighlightedEmotion}
                  setHighlightedVariable={this.setHighlightedVariable}
                  setHighlightedParticipant={this.setHighlightedParticipant}
                  setTime={this.setTime}
                />
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(Call);
